import { FC } from 'react'
import { useTranslate } from 'react-admin'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import HubIcon from '@mui/icons-material/Hub'
import StorageIcon from '@mui/icons-material/Storage'
import { DataStoreMode, DataStoreWriteNodeData, WorkflowNodeData } from '../types'
import { themeColor } from '../../../layout/theme/color'
import ExpressionTextField from '../autocomplete/ExpressionTextField'

interface DataStoreWriteConfigProps {
  data: DataStoreWriteNodeData
  onUpdate: (data: Partial<WorkflowNodeData>) => void
}

const DataStoreWriteConfig: FC<DataStoreWriteConfigProps> = ({ data, onUpdate }) => {
  const translate = useTranslate()
  const identifier = (data.identifier ?? '').trim()
  const hasContent = Boolean((data.content ?? '').trim())
  const isPlain = data.storageMode === 'plain'
  const keyValue = (data.key ?? '').trim()

  const warningText = !identifier
    ? translate('workflow.node.dataStoreMissingIdentifier')
    : isPlain && !keyValue
      ? translate('workflow.node.dataStoreMissingKey')
      : !hasContent
        ? translate('workflow.node.dataStoreMissingContent')
        : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {warningText && <Alert severity="warning">{warningText}</Alert>}

      <Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: themeColor.slate60, mb: 0.5, display: 'block' }}
        >
          {translate('workflow.config.dataStoreMode')}
        </Typography>
        <ToggleButtonGroup
          value={data.storageMode ?? 'plain'}
          exclusive
          onChange={(_e, val: DataStoreMode | null) => val && onUpdate({ storageMode: val })}
          size="small"
          fullWidth
        >
          <ToggleButton value="vector" sx={{ textTransform: 'none', gap: 0.5 }}>
            <HubIcon sx={{ fontSize: 16 }} /> {translate('workflow.config.dataStoreModeVector')}
          </ToggleButton>
          <ToggleButton value="plain" sx={{ textTransform: 'none', gap: 0.5 }}>
            <StorageIcon sx={{ fontSize: 16 }} />{' '}
            {translate('workflow.config.dataStoreModePlain')}
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography
          variant="caption"
          sx={{ color: themeColor.slate60, mt: 0.5, display: 'block', lineHeight: 1.4 }}
        >
          {translate(
            isPlain
              ? 'workflow.config.dataStoreModePlainHelp'
              : 'workflow.config.dataStoreModeVectorHelp',
          )}
        </Typography>
      </Box>

      <ExpressionTextField
        label={translate('workflow.config.label')}
        value={data.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        size="small"
        fullWidth
      />

      <ExpressionTextField
        required
        error={!identifier}
        label={translate('workflow.config.dataStoreIdentifier')}
        value={data.identifier ?? ''}
        onChange={(e) => onUpdate({ identifier: e.target.value })}
        size="small"
        fullWidth
        placeholder="paspoort-faq"
        helperText={translate('workflow.config.dataStoreIdentifierHelp')}
        InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
      />

      {isPlain && (
        <ExpressionTextField
          required
          error={!keyValue}
          label={translate('workflow.config.dataStoreKey')}
          value={data.key ?? ''}
          onChange={(e) => onUpdate({ key: e.target.value })}
          size="small"
          fullWidth
          placeholder="passport_processing_days"
          helperText={translate('workflow.config.dataStoreKeyHelp')}
          InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
        />
      )}

      <ExpressionTextField
        required
        error={!hasContent}
        label={translate(
          isPlain ? 'workflow.config.dataStoreValue' : 'workflow.config.dataStoreContent',
        )}
        value={data.content ?? ''}
        onChange={(e) => onUpdate({ content: e.target.value })}
        size="small"
        fullWidth
        multiline
        minRows={4}
        helperText={translate(
          isPlain
            ? 'workflow.config.dataStoreValueHelp'
            : 'workflow.config.dataStoreContentHelp',
        )}
      />
    </Box>
  )
}

export default DataStoreWriteConfig
